// Symptom log PDF generator.
// Outputs a single PDF with:
//   1. Cover (veteran, date range, totals)
//   2. Per-condition summary cards (avg severity, peak, count, mini severity trend)
//   3. Chronological entry log
//   4. Disclaimer footer
//
// Designed to be handed to a C&P examiner or treating provider.

import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";

const PAGE_W = 612;
const PAGE_H = 792;
const M = 48;

const C_BRAND = rgb(0.11, 0.30, 0.85);
const C_BRAND_DARK = rgb(0.06, 0.09, 0.16);
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BORDER = rgb(0.85, 0.88, 0.92);
const C_BG = rgb(0.97, 0.98, 0.99);
const C_RED = rgb(0.78, 0.18, 0.18);
const C_AMBER = rgb(0.84, 0.55, 0.10);
const C_GREEN = rgb(0.10, 0.55, 0.32);

function sanitize(s) {
  return String(s ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function wrap(text, charsPerLine) {
  return String(text || "").replace(/\r\n?/g, "\n").split("\n").flatMap((para, idx, arr) => {
    const words = para.split(" ").filter(Boolean);
    const rows = [];
    let row = "";
    words.forEach((w) => {
      if ((row + " " + w).trim().length > charsPerLine) { rows.push(row); row = w; }
      else row = (row + " " + w).trim();
    });
    if (row) rows.push(row);
    if (rows.length === 0) rows.push("");
    if (idx < arr.length - 1) rows.push("");
    return rows;
  });
}

function severityColor(sev) {
  if (sev >= 7) return C_RED;
  if (sev >= 4) return C_AMBER;
  if (sev >= 1) return C_GREEN;
  return C_MUTED;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: iso.includes("T") ? "short" : undefined });
  } catch { return iso; }
}

class Canvas {
  constructor(doc, font, bold) {
    this.doc = doc; this.font = font; this.bold = bold;
    this.pages = []; this.page = null; this.y = 0; this.section = "";
  }
  newPage(section) {
    this.page = this.doc.addPage([PAGE_W, PAGE_H]);
    this.pages.push({ page: this.page, section: section ?? this.section });
    this.section = section ?? this.section;
    this.y = PAGE_H - M;
    return this.page;
  }
  ensure(space, section) { if (!this.page || this.y - space < M + 30) this.newPage(section); }
  text(value, x, y, opts = {}) {
    const { size = 10, bold = false, color = C_TEXT, maxWidth } = opts;
    const useFont = bold ? this.bold : this.font;
    let s = sanitize(value);
    // Single-line truncate when caller passes a maxWidth (used for tabular layout
    // cells that must not bleed into the next column).
    if (maxWidth && useFont.widthOfTextAtSize(s, size) > maxWidth) {
      const ellipsis = "...";
      while (s.length > 1 && useFont.widthOfTextAtSize(s + ellipsis, size) > maxWidth) {
        s = s.slice(0, -1);
      }
      s = s + ellipsis;
    }
    this.page.drawText(s, { x, y, size, font: useFont, color });
  }
  rect(x, y, w, h, opts = {}) {
    const { fill, stroke, strokeW = 0.6 } = opts;
    this.page.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: stroke, borderWidth: stroke ? strokeW : 0 });
  }
  line(x1, y1, x2, y2, color = C_BORDER, thick = 0.7) {
    this.page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: thick, color });
  }
  heading(label, color = C_BRAND_DARK) {
    this.ensure(36);
    this.rect(M, this.y - 4, PAGE_W - M * 2, 1.4, { fill: color });
    this.y -= 18;
    this.text(label, M, this.y, { size: 13, bold: true, color });
    this.y -= 18;
  }
  sectionTitle(label) {
    this.ensure(24);
    this.text(label.toUpperCase(), M, this.y, { size: 9, bold: true, color: C_BRAND });
    this.y -= 12;
    this.line(M, this.y + 3, M + 50, this.y + 3, C_BRAND, 1.2);
    this.y -= 6;
  }
  para(text, opts = {}) {
    const { size = 10, color = C_TEXT, bold = false, width = 92, indent = 0 } = opts;
    wrap(text, width).forEach((line) => {
      this.ensure(size + 4);
      this.text(line, M + indent, this.y, { size, color, bold });
      this.y -= size + 3;
    });
  }
  severityBar(x, y, w, h, sev) {
    // background
    this.rect(x, y, w, h, { fill: C_BG, stroke: C_BORDER });
    const pct = Math.max(0, Math.min(10, sev)) / 10;
    this.rect(x, y, w * pct, h, { fill: severityColor(sev) });
  }
  miniTrend(x, y, w, h, severities) {
    // Sparkline of severities (oldest -> newest)
    this.rect(x, y, w, h, { fill: C_BG, stroke: C_BORDER });
    if (!severities.length) {
      this.text("no data", x + 4, y + h / 2 - 3, { size: 7, color: C_MUTED });
      return;
    }
    const stepX = severities.length > 1 ? w / (severities.length - 1) : 0;
    let prev = null;
    severities.forEach((s, idx) => {
      const cx = x + idx * stepX;
      const cy = y + (Math.max(0, Math.min(10, s)) / 10) * (h - 4) + 2;
      if (prev) this.line(prev.x, prev.y, cx, cy, C_BRAND, 0.9);
      this.page.drawCircle({ x: cx, y: cy, size: 1.4, color: severityColor(s) });
      prev = { x: cx, y: cy };
    });
  }
}

function drawHeader(c, info) {
  c.page.drawRectangle({ x: 0, y: PAGE_H - 28, width: PAGE_W, height: 28, color: C_BRAND_DARK });
  c.page.drawRectangle({ x: 0, y: PAGE_H - 30, width: PAGE_W, height: 2, color: C_BRAND });
  c.page.drawRectangle({ x: M, y: PAGE_H - 22, width: 18, height: 14, color: C_BRAND });
  c.text("VA", M + 3, PAGE_H - 19, { size: 8, bold: true, color: rgb(1, 1, 1) });
  c.text(info.headerTitle || "CLAIMOPS SYMPTOM LOG", M + 24, PAGE_H - 19, { size: 9, bold: true, color: rgb(1, 1, 1) });
  // Same right-aligned section + workspace pattern as masterPdf — no fixed-offset overlap.
  const baselineY = PAGE_H - 19;
  const rightEdge = PAGE_W - M;
  const wsLabel = sanitize(info.workspaceTitle || "Symptom Log");
  const wsW = c.font.widthOfTextAtSize(wsLabel, 8);
  c.text(wsLabel, rightEdge - wsW, baselineY, { size: 8, color: rgb(1, 1, 1) });
  const section = c.section || "";
  if (section) {
    const sectionW = c.font.widthOfTextAtSize(section, 8);
    const sepX = rightEdge - wsW - 10;
    c.page.drawLine({ start: { x: sepX, y: baselineY + 2 }, end: { x: sepX, y: baselineY + 9 }, thickness: 0.6, color: rgb(0.55, 0.62, 0.78) });
    c.text(section, sepX - 6 - sectionW, baselineY, { size: 8, color: rgb(0.78, 0.83, 0.92) });
  }
}

function drawWatermark(c, isFree) {
  if (!isFree) return;
  c.page.drawText("FREE - UPGRADE TO REMOVE", {
    x: 30, y: 380, size: 44,
    font: c.bold, color: rgb(0.92, 0.62, 0.62),
    rotate: degrees(34), opacity: 0.45,
  });
}

function drawFooter(c, pageNum, total, info) {
  c.line(M, 50, PAGE_W - M, 50);
  c.text("ClaimOps VA Symptom Log - Veteran-reported. NOT an official medical record. For C&P / provider review.", M, 36, { size: 7, color: C_MUTED });
  const ps = `Page ${pageNum} of ${total}`;
  const w = c.font.widthOfTextAtSize(ps, 7);
  c.text(ps, PAGE_W - M - w, 36, { size: 7, color: C_MUTED, bold: true });
  c.text(info.generatedAt, M, 24, { size: 7, color: C_MUTED });
}

// ---- Page builders ----

function buildCover(c, info) {
  c.newPage("Cover");
  c.page.drawRectangle({ x: 0, y: PAGE_H - 220, width: PAGE_W, height: 220, color: C_BRAND_DARK });
  c.page.drawRectangle({ x: 0, y: PAGE_H - 224, width: PAGE_W, height: 4, color: C_BRAND });
  c.page.drawRectangle({ x: M, y: PAGE_H - 110, width: 60, height: 60, color: C_BRAND });
  c.text("VA", M + 12, PAGE_H - 92, { size: 24, bold: true, color: rgb(1, 1, 1) });
  c.text("CLAIMOPS", M + 78, PAGE_H - 78, { size: 20, bold: true, color: rgb(1, 1, 1) });
  c.text("Symptom Log", M + 78, PAGE_H - 100, { size: 12, color: rgb(0.78, 0.85, 0.96) });
  c.text("VETERAN-REPORTED SYMPTOM HISTORY", M, PAGE_H - 160, { size: 11, bold: true, color: rgb(0.62, 0.74, 0.95) });
  c.text(info.workspaceTitle || "VA Claim", M, PAGE_H - 192, { size: 22, bold: true, color: rgb(1, 1, 1) });

  let y = PAGE_H - 270;
  c.text("PREPARED FOR", M, y, { size: 9, bold: true, color: C_BRAND }); y -= 14;
  c.text(info.veteranName || "Veteran", M, y, { size: 16, bold: true }); y -= 18;
  if (info.email) { c.text(info.email, M, y, { size: 10, color: C_MUTED }); y -= 14; }

  y = PAGE_H - 380;
  const cells = [
    { label: "ENTRIES", value: `${info.totalEntries}` },
    { label: "DATE RANGE", value: info.dateRange || "n/a" },
    { label: "AVG SEVERITY", value: info.totalEntries ? info.avgSeverity.toFixed(1) : "—" },
    { label: "PEAK SEVERITY", value: info.totalEntries ? `${info.peakSeverity}/10` : "—" },
  ];
  const cellW = (PAGE_W - M * 2 - 36) / cells.length;
  cells.forEach((cell, i) => {
    const x = M + i * (cellW + 12);
    c.rect(x, y - 64, cellW, 64, { fill: C_BG, stroke: C_BORDER });
    c.text(cell.label, x + 10, y - 20, { size: 7, bold: true, color: C_MUTED });
    c.text(cell.value, x + 10, y - 50, { size: 18, bold: true, color: C_BRAND_DARK });
  });

  y = 240;
  c.text("HOW TO USE THIS DOCUMENT", M, y, { size: 9, bold: true, color: C_BRAND }); y -= 14;
  const tips = [
    "Bring this packet to your VA Compensation & Pension (C&P) exam, your treating provider, or your VSO.",
    "It documents what you have been experiencing day-to-day - frequency, severity, impact on work / sleep / function.",
    "Each entry is veteran-reported. Pair it with medical records, statements, and lab results in your claim file.",
    "Examiners can scan the per-condition summary or read the full chronological log for pattern detail.",
  ];
  tips.forEach((t) => {
    wrap(t, 92).forEach((line) => { c.text(line, M + 6, y, { size: 10 }); y -= 13; });
  });

  c.text(`Generated: ${info.generatedAt}`, M, 110, { size: 9, color: C_MUTED });
  c.text("ClaimOps VA", PAGE_W - M - c.bold.widthOfTextAtSize("ClaimOps VA", 9), 110, { size: 9, bold: true, color: C_BRAND });
}

// VA C&P body-system breakdown page. Groups entries by `bodySystem`, ranks each
// system by total entry count, and inside the system ranks symptoms by frequency
// then avg severity. Includes a 14-entry severity sparkline per symptom.
// Skipped (returns silently) when no entries carry a bodySystem code — keeps
// backwards compatibility with old free-text logs.
function buildCpBodySystemSummary(c, info) {
  const tagged = (info.entries || []).filter((e) => e.bodySystem);
  if (tagged.length === 0) return;

  c.newPage("C&P Symptom Summary by Body System");
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading("C&P Symptom Summary by Body System");
  c.para(
    "VA C&P–aligned breakdown. Each row uses the symptom term a C&P examiner " +
    "writes on the Disability Benefits Questionnaire (DBQ). Sorted by body system " +
    "and then by frequency, so a rater can scan the strongest signals first.",
    { size: 9 },
  );
  c.y -= 4;

  // Group by bodySystem
  const bySystem = new Map();
  tagged.forEach((e) => {
    const key = e.bodySystem || "other";
    if (!bySystem.has(key)) bySystem.set(key, []);
    bySystem.get(key).push(e);
  });
  // Sort systems by total entries desc
  const systems = Array.from(bySystem.entries()).sort((a, b) => b[1].length - a[1].length);

  // Pretty labels (mirror lib/symptomCatalog.js — keep in sync if catalog changes)
  const SYS_LABEL = {
    msk: "Musculoskeletal", mental: "Mental Health", neuro: "Neurological",
    audio: "Hearing / Tinnitus", ent: "ENT", eye: "Eyes",
    cardio: "Cardiovascular", pulm: "Respiratory", gi: "Gastrointestinal",
    gu: "Genitourinary", skin: "Skin", endo: "Endocrine / Metabolic",
    heme: "Hematological / Immune",
  };

  systems.forEach(([sysId, sysEntries]) => {
    c.ensure(60);
    // System header
    c.text(SYS_LABEL[sysId] || sysId.toUpperCase(), M, c.y, { size: 12, bold: true, color: C_BRAND_DARK });
    c.text(`${sysEntries.length} log${sysEntries.length === 1 ? "" : "s"}`, PAGE_W - M - 80, c.y, { size: 9, color: C_MUTED });
    c.y -= 18;
    c.line(M, c.y, PAGE_W - M, c.y);
    c.y -= 6;

    // Roll up by symptom label
    const byLabel = new Map();
    sysEntries.forEach((e) => {
      const lbl = e.symptom || "(unspecified)";
      if (!byLabel.has(lbl)) byLabel.set(lbl, []);
      byLabel.get(lbl).push(e);
    });
    const rows = Array.from(byLabel.entries())
      .map(([label, arr]) => {
        const severities = arr.map((x) => Number(x.severity || 0));
        const avg = severities.reduce((s, v) => s + v, 0) / arr.length;
        const peak = Math.max(...severities, 0);
        const last = arr.map((x) => x.date).sort().slice(-1)[0];
        // 14-entry sparkline (oldest -> newest in time)
        const trend = arr.slice().sort((a, b) => new Date(a.date) - new Date(b.date)).map((x) => x.severity || 0).slice(-14);
        return { label, count: arr.length, avg, peak, last, trend, code: arr[0].symptomCode || "" };
      })
      .sort((a, b) => b.count - a.count || b.avg - a.avg);

    rows.forEach((r) => {
      c.ensure(22);
      const lineY = c.y;
      // Label
      c.text(r.label, M, lineY, { size: 9.5, bold: true, color: C_BRAND_DARK });
      // VA code chip if present
      if (r.code) {
        const labelW = c.bold.widthOfTextAtSize(sanitize(r.label), 9.5);
        c.text(r.code, M + labelW + 6, lineY, { size: 7, color: C_MUTED });
      }
      // Right-hand stats
      const statsX = PAGE_W - M - 230;
      c.text(`${r.count}x`, statsX, lineY, { size: 9, bold: true });
      c.text(`avg ${r.avg.toFixed(1)}`, statsX + 30, lineY, { size: 9, color: C_MUTED });
      c.text(`peak ${r.peak}`, statsX + 80, lineY, { size: 9, color: C_MUTED });
      // Sparkline
      c.miniTrend(PAGE_W - M - 90, lineY - 7, 80, 10, r.trend);
      c.y -= 16;
    });
    c.y -= 6;
  });
}

function buildPerConditionSummary(c, info) {
  c.newPage("Per-Condition Summary");
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading("Per-Condition Summary");
  c.para(
    "Each card aggregates the entries linked to a single condition: total entries, average and peak severity, " +
    "and a mini severity trend line (oldest -> newest). Unlinked entries are grouped under 'General / unspecified.'",
    { size: 9 },
  );
  c.y -= 6;

  const groups = info.groups || [];
  if (groups.length === 0) {
    c.para("No symptom entries logged yet.", { color: C_MUTED, bold: true });
    return;
  }
  groups.forEach((g) => {
    c.ensure(110);
    const cardY = c.y;
    const cardH = 100;
    c.rect(M, cardY - cardH, PAGE_W - M * 2, cardH, { fill: C_BG, stroke: C_BORDER });
    // Title row
    c.text(g.name, M + 12, cardY - 20, { size: 12, bold: true, color: C_BRAND_DARK });
    if (g.code) c.text(`DC ${g.code}`, M + 12 + c.bold.widthOfTextAtSize(sanitize(g.name), 12) + 8, cardY - 20, { size: 9, color: C_MUTED });
    if (g.isUndx) {
      const labelW = c.bold.widthOfTextAtSize(sanitize(g.name), 12);
      const pillX = M + 12 + labelW + 10;
      c.rect(pillX, cardY - 24, 70, 12, { fill: rgb(0.99, 0.95, 0.78), stroke: C_AMBER });
      c.text("UNDIAGNOSED", pillX + 5, cardY - 22, { size: 7, bold: true, color: C_AMBER });
    }
    c.text(`${g.entries.length} entr${g.entries.length === 1 ? "y" : "ies"}`, PAGE_W - M - 90, cardY - 20, { size: 9, bold: true, color: C_BRAND });

    // Stats row
    const statY = cardY - 44;
    const stats = [
      { label: "AVG", value: g.avgSeverity.toFixed(1) },
      { label: "PEAK", value: `${g.peakSeverity}/10` },
      { label: "LAST", value: g.lastDate ? formatDate(g.lastDate).split(",")[0] : "—" },
      { label: "FLARES", value: `${g.flareCount}` },
    ];
    stats.forEach((s, i) => {
      const x = M + 12 + i * 84;
      c.text(s.label, x, statY, { size: 7, bold: true, color: C_MUTED });
      c.text(s.value, x, statY - 14, { size: 13, bold: true, color: C_BRAND_DARK });
    });

    // Severity bar (avg)
    c.text("AVG SEVERITY", M + 12, cardY - 78, { size: 7, bold: true, color: C_MUTED });
    c.severityBar(M + 12, cardY - 92, 180, 8, g.avgSeverity);
    c.text(`${g.avgSeverity.toFixed(1)} / 10`, M + 200, cardY - 88, { size: 9, bold: true });

    // Mini trend (right side)
    c.text("SEVERITY TREND", PAGE_W - M - 180, cardY - 78, { size: 7, bold: true, color: C_MUTED });
    const trend = g.entries.slice().sort((a, b) => new Date(a.date) - new Date(b.date)).map((e) => e.severity || 0);
    c.miniTrend(PAGE_W - M - 180, cardY - 94, 168, 12, trend);

    c.y = cardY - cardH - 12;
  });
}

// Compact tabular log row. ~14pt tall per entry — fits 35-40 entries per page
// vs the old verbose layout that hit 8 entries per page.
function tabularLogRow(c, e, issuesById, cols, colXs) {
  c.ensure(18);
  // Left rail — severity color
  c.rect(M, c.y - 12, 2.5, 14, { fill: severityColor(e.severity || 0) });
  // Date (short)
  const dateShort = formatDate(e.date).split(",")[0]; // "May 15"
  c.text(dateShort, colXs[0], c.y - 8, { size: 8, color: C_TEXT });
  // Type pill
  const type = e.entryType === "flare" ? "FLR" : "DLY";
  c.text(type, colXs[1], c.y - 8, { size: 8, bold: true, color: e.entryType === "flare" ? C_RED : C_BRAND });
  // Sev / mood
  c.text(`${e.severity ?? 0}/10`, colXs[2], c.y - 8, { size: 8, bold: true, color: severityColor(e.severity || 0) });
  c.text(e.mood != null ? `${e.mood}/10` : "—", colXs[3], c.y - 8, { size: 8, color: C_MUTED });
  // Condition (truncated)
  let cond;
  if (e.linkedIssueId) cond = issuesById[e.linkedIssueId]?.claimedAs || "Linked";
  else if (e.isUndiagnosed) cond = e.suspectedCondition ? `~ ${e.suspectedCondition}` : "Undx";
  else cond = "General";
  c.text(cond, colXs[4], c.y - 8, { size: 8, color: e.isUndiagnosed ? C_AMBER : C_TEXT, bold: e.isUndiagnosed, maxWidth: cols[4].width - 4 });
  // Symptom (truncated)
  c.text(e.symptom || "—", colXs[5], c.y - 8, { size: 8, color: C_TEXT, maxWidth: cols[5].width - 4 });
  // Impact (joined, truncated)
  const impactStr = (e.impact || []).join(", ") || "—";
  c.text(impactStr, colXs[6], c.y - 8, { size: 8, color: C_MUTED, maxWidth: cols[6].width - 4 });
  c.y -= 14;
  c.line(M, c.y + 2, PAGE_W - M, c.y + 2, C_BORDER, 0.3);
  c.y -= 2;
}

function buildLog(c, info) {
  c.newPage("Chronological Log");
  drawHeader(c, info);
  drawWatermark(c, c.isFree);
  c.y = PAGE_H - 60;
  c.heading("Chronological Symptom Log");
  c.para(
    "Each row is a single veteran-reported entry, newest first. Severity rail: red = severe (7-10), amber = moderate (4-6), green = mild (1-3). Type: DLY = daily check-in, FLR = flare.",
    { size: 9 },
  );
  c.y -= 6;

  // Column layout (must sum to <= 516 = PAGE_W - 2*M = 612 - 96).
  // 60 + 30 + 38 + 38 + 110 + 130 + 110 = 516.
  const cols = [
    { label: "DATE",      width: 60 },
    { label: "TYPE",      width: 30 },
    { label: "SEV",       width: 38 },
    { label: "MOOD",      width: 38 },
    { label: "CONDITION", width: 110 },
    { label: "SYMPTOM",   width: 130 },
    { label: "IMPACT",    width: 110 },
  ];
  const colXs = [];
  let acc = M;
  cols.forEach((col) => { colXs.push(acc + 4); acc += col.width; });

  // Header row
  c.ensure(20);
  c.rect(M, c.y - 4, PAGE_W - M * 2, 16, { fill: C_BG });
  cols.forEach((col, i) => {
    c.text(col.label, colXs[i], c.y + 4, { size: 7, bold: true, color: C_MUTED });
  });
  c.y -= 14;
  c.line(M, c.y + 2, PAGE_W - M, c.y + 2, C_BORDER, 0.6);
  c.y -= 2;

  const issuesById = {};
  (info.issues || []).forEach((i) => { issuesById[i.id] = i; });

  if (!info.entries.length) {
    c.para("No symptom entries logged yet. Start adding entries in the Symptom Tracker view.", { color: C_MUTED, bold: true });
    return;
  }
  info.entries.forEach((e) => tabularLogRow(c, e, issuesById, cols, colXs));

  // Expanded-detail appendix: only include entries that have notes / triggers /
  // body regions / coping — provider can flip here if they want full context.
  const detailed = info.entries.filter((e) => (e.notes || (e.triggers && e.triggers.length) || (e.bodyRegions && e.bodyRegions.length) || e.copingUsed));
  if (detailed.length > 0) {
    c.newPage("Entry Notes");
    drawHeader(c, info);
    drawWatermark(c, c.isFree);
    c.y = PAGE_H - 60;
    c.heading("Entry Notes (free-text)");
    c.para("Only entries that recorded free-text triggers, body regions, coping methods, or notes appear below.", { size: 9 });
    c.y -= 4;
    detailed.forEach((e) => {
      c.ensure(40);
      const dateShort = formatDate(e.date).split(",")[0];
      c.rect(M, c.y - 4, 2.5, 14, { fill: severityColor(e.severity || 0) });
      c.text(`${dateShort}  -  ${e.symptom || "(no symptom)"}`, M + 8, c.y, { size: 9, bold: true });
      c.y -= 14;
      const tags = [];
      if (e.triggers?.length) tags.push(`Triggers: ${e.triggers.join(", ")}`);
      if (e.bodyRegions?.length) tags.push(`Body: ${e.bodyRegions.join(", ")}`);
      if (e.copingUsed) tags.push(`Coping: ${e.copingUsed}`);
      if (e.duration) tags.push(`Duration: ${e.duration}`);
      tags.forEach((t) => {
        wrap(t, 100).forEach((line) => { c.ensure(11); c.text(line, M + 10, c.y, { size: 8, color: C_TEXT }); c.y -= 10; });
      });
      if (e.notes) {
        wrap(`Notes: ${e.notes}`, 100).forEach((line) => { c.ensure(11); c.text(line, M + 10, c.y, { size: 8, color: C_MUTED }); c.y -= 10; });
      }
      c.y -= 4;
      c.line(M, c.y, PAGE_W - M, c.y, C_BORDER, 0.3);
      c.y -= 8;
    });
  }
}

function buildDisclaimer(c, info) {
  c.newPage("Disclaimer");
  drawHeader(c, info);
  c.y = PAGE_H - 60;
  c.heading("Important Disclaimer", C_RED);
  const paras = [
    "This document is a VETERAN-REPORTED symptom journal generated by ClaimOps VA. It is NOT a medical record, NOT a diagnosis, and NOT a substitute for clinical examination.",
    "ClaimOps VA is a planning and organization tool. It is not a law firm, claims agent, VSO, or medical provider. No attorney-client or provider-patient relationship is created by using this app.",
    "Entries reflect what the veteran logged on the dates shown. Severity scores (0-10) and impact descriptions are subjective self-reports.",
    "Examiners, providers, and adjudicators should pair this log with the official medical record, service treatment record, and any C&P exam findings before making any determination.",
    "All rating criteria and diagnostic codes must be independently verified at VA.gov and eCFR.gov.",
  ];
  paras.forEach((p) => { c.para(p, { size: 10, width: 92 }); c.y -= 4; });
}

// ---- Public entrypoint ----
export async function createSymptomLogPdf({
  workspace, profile, master526ez, issues, symptoms, isPro = true,
}) {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const c = new Canvas(pdfDoc, font, bold);

  const entries = (symptoms || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  // Aggregates
  const severities = entries.map((e) => Number(e.severity || 0));
  const totalEntries = entries.length;
  const avgSeverity = totalEntries ? severities.reduce((s, v) => s + v, 0) / totalEntries : 0;
  const peakSeverity = totalEntries ? Math.max(...severities) : 0;
  const dates = entries.map((e) => e.date).sort();
  const dateRange = totalEntries
    ? `${formatDate(dates[0]).split(",")[0]} - ${formatDate(dates[dates.length - 1]).split(",")[0]}`
    : "";

  // Per-condition groups
  const groupMap = new Map();
  entries.forEach((e) => {
    let key;
    if (e.linkedIssueId) key = e.linkedIssueId;
    else if (e.isUndiagnosed) key = `__undx__${(e.suspectedCondition || "").toLowerCase().trim()}`;
    else key = "__general__";
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key).push(e);
  });
  const groups = [];
  groupMap.forEach((arr, key) => {
    let name, code = "", isUndx = false;
    if (key.startsWith("__undx__")) {
      const guess = arr[0].suspectedCondition;
      name = guess ? `Undiagnosed: ${guess}` : "Undiagnosed symptoms (no suspected dx)";
      isUndx = true;
    } else if (key === "__general__") {
      name = "General / unspecified";
    } else {
      const issue = (issues || []).find((i) => i.id === key);
      name = issue ? issue.claimedAs : "Linked condition";
      code = issue ? issue.code : "";
    }
    const ss = arr.map((e) => Number(e.severity || 0));
    const avg = ss.reduce((s, v) => s + v, 0) / arr.length;
    const peak = Math.max(...ss, 0);
    const lastDate = arr.map((e) => e.date).sort().slice(-1)[0];
    const flareCount = arr.filter((e) => e.entryType === "flare").length;
    groups.push({ key, name, code, isUndx, entries: arr, avgSeverity: avg, peakSeverity: peak, lastDate, flareCount });
  });
  // Sort: claimed conditions first (by peak severity desc), then undiagnosed, then general
  groups.sort((a, b) => {
    const tier = (g) => (g.key === "__general__" ? 2 : g.isUndx ? 1 : 0);
    const ta = tier(a), tb = tier(b);
    if (ta !== tb) return ta - tb;
    return b.peakSeverity - a.peakSeverity;
  });

  const info = {
    workspaceTitle: workspace?.title || "VA Claim Workspace",
    veteranName: master526ez?.veteranName || profile?.name || "Veteran",
    email: master526ez?.email || "",
    generatedAt: new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }),
    totalEntries,
    avgSeverity,
    peakSeverity,
    dateRange,
    entries,
    issues: issues || [],
    groups,
  };

  buildCover(c, info);
  c.isFree = !isPro;
  buildCpBodySystemSummary(c, info);
  buildPerConditionSummary(c, info);
  buildLog(c, info);
  buildDisclaimer(c, info);

  const total = c.pages.length;
  c.pages.forEach((p, idx) => {
    c.page = p.page;
    c.section = p.section;
    if (idx > 0) drawFooter(c, idx + 1, total, info);
    else c.line(M, 60, PAGE_W - M, 60);
  });

  return await pdfDoc.save();
}

// Shared primitives consumed by lib/symptomEvidencePdf.js so the Evidence
// Summary + C&P Prep exports stay visually identical to the Symptom Log.
export {
  Canvas, sanitize, wrap, formatDate, severityColor,
  drawHeader, drawWatermark, drawFooter,
  PAGE_W, PAGE_H, M,
  C_BRAND, C_BRAND_DARK, C_TEXT, C_MUTED, C_BORDER, C_BG, C_RED, C_AMBER, C_GREEN,
};
